import keyBy from 'lodash-es/keyBy';
import { computed, ref } from 'vue';

import { PaginatedResponse } from '@/models/api';
import { BillingInvoice } from '@/models/billing';
import {
  Application,
  CompanyLicense,
  CompanyLicenseWithStatus,
  License,
  LicenseType,
  PaymentMethod,
  PaymentTerm,
} from '@/models/company';
import { Distributor } from '@/models/distributor';
import { Staff, StaffWithPermissions } from '@/models/staff';
import { Store } from '@/models/store';
import { TaxSchedule } from '@/pages/Settings/Taxes/Taxes.models';
import { SettingsBrand } from '@/services/api/models/settings';

export const setupSettingsState = () => {
  const applications = ref<Application[]>([]);
  const billingInvoices = ref<BillingInvoice[]>([]);
  const brands = ref<SettingsBrand[]>([]);
  const companyLicenses = ref<{
    active: CompanyLicense[];
    archived: CompanyLicense[];
    pending: CompanyLicense[];
  }>({
    active: [],
    archived: [],
    pending: [],
  });
  const distributors = ref<Distributor[]>([]);
  const isLoadingBillingInvoices = ref(false);
  const isLoadingCompanyLicenses = ref(false);
  const isLoadingPendingStoreRequests = ref(false);
  const isLoadingTaxSchedules = ref(false);
  const isLoadingBillingPaymentMethods = ref(false);
  const isLoadingBrands = ref(false);
  const licenses = ref<License[]>([]);
  const licenseTypes = ref<LicenseType[]>([]);
  const paginatedTaxSchedules = ref<PaginatedResponse<TaxSchedule>>({
    count: 0,
    results: [],
  });
  const paymentMethods = ref<PaymentMethod[]>([]);
  const paymentTerms = ref<PaymentTerm[]>([]);
  const pendingStoreRequests = ref<Store[]>([]);
  const staff = ref<Staff[]>([]);
  const staffWithPermissions = ref<StaffWithPermissions[]>([]);

  const getProductBrand = computed(
    () => (productBrandId: number) => brands.value.find(({ id }) => id === productBrandId),
  );

  /**
   * Returns array of BME distributors which have enabled orders creation.
   */
  const distributorsWithOrderCreation = computed(
    () =>
      distributors.value.filter((distributor) =>
        distributor.brands?.find((brand) => brand.allowBrandOwnerOrders === true),
      ) || undefined,
  );

  /**
   * Returns BME distributor by given id
   * @param {number} id distributor id
   */
  const getDistributor = computed(() => (id: number) => {
    const distributors = distributorsWithOrderCreation.value;

    return distributors.find((distributor) => distributor.id === id);
  });

  /**
   * Returns array of BME brands for given distributor id
   * @param {number} id distributor id
   */
  const getDistributorBrands = computed(() => (id: number) => brands.value.filter((brand) => brand.company === id));

  /**
   * Returns sales reps
   * @param {String} key value that serves as key for the object
   * @param {Boolean} makeObject flag to return either an object or an array
   */
  const getSalesRep = computed(() => (key = 'id', makeObject = false) => {
    return makeObject ? keyBy(staff.value, key) : staff.value;
  });

  /**
   * Returns flat array of company license objects with status property on each one
   */
  const companyLicensesWithStatuses = computed(() =>
    Object.entries(companyLicenses.value).reduce<CompanyLicenseWithStatus[]>(
      (flatLicensesWithStatuses, [status, licenses]) => {
        const licensesWithStatuses = licenses.map((license) => ({
          ...license,
          status,
        }));

        return flatLicensesWithStatuses.concat(licensesWithStatuses);
      },
      [],
    ),
  );

  return {
    applications,
    billingInvoices,
    brands,
    companyLicenses,
    distributors,
    distributorsWithOrderCreation,
    isLoadingBillingInvoices,
    isLoadingCompanyLicenses,
    isLoadingPendingStoreRequests,
    isLoadingTaxSchedules,
    isLoadingBillingPaymentMethods,
    isLoadingBrands,
    licenses,
    licenseTypes,
    paginatedTaxSchedules,
    paymentMethods,
    paymentTerms,
    pendingStoreRequests,
    staff,
    staffWithPermissions,

    getProductBrand,
    getDistributorBrands,
    getDistributor,
    getSalesRep,
    companyLicensesWithStatuses,
  };
};

export type SettingsState = ReturnType<typeof setupSettingsState>;
